<script setup lang="ts">
// The Repair-vs-Replace meter: a ¾ doughnut gauge with center text.
// Colors come from the site tokens at build time (canvas can't read var()).
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ArcElement, Chart, DoughnutController, Tooltip, type ChartConfiguration } from 'chart.js'
import { chartTheme, prefersReducedMotion, withAlpha } from '@/shared/charts/theme'

Chart.register(DoughnutController, ArcElement, Tooltip)

const props = defineProps<{ repairScore: number }>()

const canvas = ref<HTMLCanvasElement | null>(null)
let chart: Chart<'doughnut'> | null = null

function build() {
    if (!canvas.value) return
    chart?.destroy()
    const t = chartTheme()
    const repair = Math.max(0, Math.min(100, props.repairScore))
    const replace = 100 - repair

    const centerText = {
        id: 'centerText',
        afterDraw(c: Chart) {
            const { ctx, chartArea } = c
            const cx = (chartArea.left + chartArea.right) / 2
            const cy = chartArea.top + (chartArea.bottom - chartArea.top) * 0.62
            ctx.save()
            ctx.textAlign = 'center'
            ctx.fillStyle = t.textStrong
            ctx.font = `700 2.1rem ${t.fontBody}`
            ctx.fillText(`${repair}%`, cx, cy)
            ctx.fillStyle = t.text
            ctx.font = `500 0.85rem ${t.fontBody}`
            ctx.fillText('repair', cx, cy + 24)
            ctx.restore()
        },
    }

    const config: ChartConfiguration<'doughnut'> = {
        type: 'doughnut',
        data: {
            labels: ['Repair', 'Replace'],
            datasets: [
                {
                    data: [repair, replace],
                    backgroundColor: [t.accent, withAlpha(t.danger, 0.75)],
                    borderWidth: 0,
                    borderRadius: 6,
                },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            circumference: 270,
            rotation: -135,
            cutout: '72%',
            animation: prefersReducedMotion() ? false : { duration: 700 },
            plugins: { tooltip: { enabled: false } },
        },
        plugins: [centerText],
    }
    chart = new Chart(canvas.value, config)
}

onMounted(build)
watch(() => props.repairScore, build)
onBeforeUnmount(() => chart?.destroy())
</script>

<template>
    <div class="gauge">
        <div class="canvas-wrap"><canvas ref="canvas"></canvas></div>
        <div class="legend">
            <span class="key"><i class="dot repair"></i> Repair {{ Math.round(repairScore) }}%</span>
            <span class="key"><i class="dot replace"></i> Replace {{ 100 - Math.round(repairScore) }}%</span>
        </div>
    </div>
</template>

<style scoped lang="scss">
.gauge {
    .canvas-wrap {
        position: relative;
        height: 220px;
    }

    .legend {
        display: flex;
        justify-content: center;
        gap: var(--site-space-lg);
        margin-top: var(--site-space-xs);
        font-size: 0.9rem;
        color: var(--site-text-muted);
    }

    .dot {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin-right: 6px;

        &.repair {
            background: var(--site-accent);
        }

        &.replace {
            background: var(--site-danger);
        }
    }
}
</style>
